import { Modal } from "react-bootstrap";

interface Props {
  show: boolean;
  /** Name of the population being selected, or null when clearing the selection. */
  targetName: string | null;
  /** Other live campaigns already using the target population. */
  sharedCount: number;
  /** Whether the campaign already points at a (different) population. */
  changingExisting: boolean;
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/*
    Selecting or changing a campaign's population is allowed — populations are
    shared across campaigns by design — but it is consequential enough that an
    accidental pick shouldn't slip through silently. This modal is the giant
    warning that replaced the old hard lock.
*/
export default function ModalPopulationChangeWarning({
  show,
  targetName,
  sharedCount,
  changingExisting,
  isSaving,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <Modal centered show={show} onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill text-warning" />
          {targetName === null ? "Remove this campaign's population?" : "Change this campaign's population?"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {sharedCount > 0 && targetName !== null && (
          <div className="alert alert-warning">
            <strong>{targetName}</strong> is already in use by {sharedCount} other{" "}
            {sharedCount === 1 ? "campaign" : "campaigns"}. Campaigns can share a
            population — they draw from the same roster of people — but make sure
            this is the population you mean. Selecting it here does not affect the
            other {sharedCount === 1 ? "campaign" : "campaigns"}.
          </div>
        )}
        {changingExisting && (
          <p className="mb-0">
            This campaign already points at a population.{" "}
            {targetName === null
              ? "Removing it leaves the campaign with no population until a new one is chosen."
              : "Audiences already built here will be re-derived against the new population on the next recalculation."}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
        <button type="button" className="btn btn-warning" onClick={onConfirm} disabled={isSaving}>
          {targetName === null ? "Remove population" : "Change population"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
